<template>
  <div class="operation-report-show-body-form-item flex flex-col">
    <div
      class="form-item-label aisea-text-16-700-dark-gray"
      :class="{
        required: required,
        'is-editing': isEditing,
        'has-errors': hasErrors,
      }"
    >
      {{ label }}
    </div>

    <div class="form-item-value-unit-edit flex aisea-text-16-400-dark-gray">
      <div class="form-item-value-edit w-full flex flex-col">
        <div class="flexible-time-options flex">
          <div
            v-for="opt in options"
            :key="opt.key"
            class="flexible-time-option"
          >
            <input
              type="radio"
              v-model="mode"
              :id="`${nameRadio}-${opt.key}`"
              :name="nameRadio"
              :value="opt.key"
              :disabled="disabled"
            />
            <label :for="`${nameRadio}-${opt.key}`">{{ opt.label }}</label>
          </div>
        </div>
        <div v-if="mode === 'DETAIL'" class="flexible-time-detail">
          <input
            type="time"
            v-model="detailTime"
            :disabled="disabled"
            class="flexible-time-input"
          />
        </div>
        <input
          class="validate-hidden-input"
          type="hidden"
          :name="name"
          v-model="value"
          v-validate="vValidate"
          :disabled="disabled"
        />
      </div>
    </div>
    <div
      class="nullable-label flex items-center aisea-text-16-400-dark-gray"
      v-if="hasNullableLabel"
    >
      <vs-checkbox
        v-model="nullableChecked"
        :color="checkBoxColor"
        :disabled="disabled"
        @change="onChange"
      >
        <span>{{ nullableLabel }}</span>
      </vs-checkbox>
    </div>
  </div>
</template>

<script>
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { ColorMixin } from "@/mixins";

const { AISEA_COLOR } = ColorMixin.data();

const EVENTS = { CHANGE: "change" };
// FLEXIBLE_TIME_PICKER(16) の値: "AM" / "PM" / "UNDECIDED" / "HH:MM"（value_string）
const PRESETS = ["AM", "PM", "UNDECIDED"];
const TIME_RE = /^([01]?\d|2[0-3]):[0-5]\d$/;

export default {
  name: "FormItemFlexibleTime",
  inheritAttrs: false,
  props: {
    label: { type: String, default: "" },
    required: { type: Boolean, default: false },
    name: { type: String, required: true }, // validation のために必要
    initialValue: { type: String, required: false },
    unit: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    isEditing: { type: Boolean, default: false },
    nullableLabel: { type: String, required: false },
    isNullableChecked: { type: Boolean, default: false },
    hasReportFormVersionItemValue: { type: Boolean, default: false },
    defaultValue: { type: String, required: false },
  },
  data() {
    return {
      mode: null, // "AM" | "PM" | "UNDECIDED" | "DETAIL"
      detailTime: "", // "HH:MM"
      value: "", // validation 用のシリアライズ値
      nullableChecked: false,
    };
  },
  watch: {
    initialValue: {
      handler() {
        this.applyInitialValue();
      },
      immediate: true,
    },
    isNullableChecked: {
      handler() {
        this.nullableChecked = this.isNullableChecked;
      },
      immediate: true,
    },
    mode() {
      this.recompute();
    },
    detailTime() {
      this.recompute();
    },
    vValidate() {
      this.$nextTick(async () => {
        await this.$validator.validateAll();
      });
    },
  },
  computed: {
    options() {
      return [
        { key: "AM", label: "午前" },
        { key: "PM", label: "午後" },
        { key: "UNDECIDED", label: "未定" },
        { key: "DETAIL", label: "詳細時刻" },
      ];
    },
    nameRadio() {
      return `radio-name-${this.name}`; // validation 用の name と分ける
    },
    emittedValue() {
      if (this.mode === "DETAIL") {
        return TIME_RE.test(this.detailTime) ? this.detailTime : "";
      }
      if (PRESETS.includes(this.mode)) return this.mode;
      return "";
    },
    hasUnit() {
      return isString(this.unit) && !isEmpty(this.unit);
    },
    isNullable() {
      if (!this.hasNullableLabel) return !this.required;
      return this.nullableChecked === true;
    },
    vValidate() {
      return this.required && !this.isNullable ? "required" : "";
    },
    hasErrors() {
      return this.errors.has(this.name);
    },
    hasNullableLabel() {
      return isString(this.nullableLabel) && !isEmpty(this.nullableLabel);
    },
    checkBoxColor() {
      return AISEA_COLOR.BLUE;
    },
  },
  methods: {
    applyInitialValue() {
      const v = this.initialValue;
      if (PRESETS.includes(v)) {
        this.mode = v;
        this.detailTime = "";
      } else if (isString(v) && TIME_RE.test(v)) {
        this.mode = "DETAIL";
        this.detailTime = v;
      } else {
        this.mode = null;
        this.detailTime = "";
      }
      this.value = this.emittedValue;
    },
    recompute() {
      this.value = this.emittedValue;
      this.onChange();
    },
    onChange() {
      if (this.disabled) return;
      const v = this.emittedValue;
      this.$emit(EVENTS.CHANGE, {
        value: v === "" ? null : v,
        nullableChecked: this.nullableChecked,
      });
    },
  },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/aisea/_variables.scss";

.operation-report-show-body-form-item {
  min-height: 90px;
  padding: 18px 0;
  row-gap: 4px;

  .form-item-value-unit-edit {
    gap: 18px;
    padding-right: 18px;
  }

  .flexible-time-options {
    gap: 18px;
    flex-wrap: wrap;

    .flexible-time-option {
      display: flex;
      gap: 6px;
      align-items: center;
    }
  }

  .flexible-time-detail {
    margin-top: 8px;
  }

  .flexible-time-input {
    padding: 10px 12px;
    border: 1px solid $aisea-light-gray;
    border-radius: 6px;
    font-size: 16px;
  }

  .form-item-label.is-editing {
    &.has-errors {
      color: $aisea-red;
    }
    &.required:after {
      content: " *";
      font-size: 14px;
      color: $aisea-red;
      font-weight: 700;
      font-family: $aisea-font-family;
    }
  }
  .nullable-label {
    gap: 20px;
  }
}
</style>
